
import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Array<object>} all variants for instant client-side lookup */
  #allVariants = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      } else {
        this.#checkedIndices.push([]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
    this.#loadAllVariants();
    this.updateAllAvailability();
    setTimeout(() => this.#selectDefaultQueenCategory());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  // ── Optimistic update helpers ────────────────────────────────────────────

  #loadAllVariants() {
    const el = this.querySelector('script[data-all-variants]');
    if (!el?.textContent) return;
    try { this.#allVariants = JSON.parse(el.textContent); } catch {}
  }

  #shouldSelectDefaultQueenCategory() {
    const isMainProductPicker =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');

    if (!isMainProductPicker) return false;

    const categoryIndex = this.#getCategoryFieldsetIndex();
    if (categoryIndex !== -1) {
      const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
      const selected = /** @type {HTMLInputElement | null} */ (
        fieldsets[categoryIndex]?.querySelector('input:checked')
      );
      if (selected?.value?.trim().toLowerCase() === 'double') return true;
    }

    try {
      const params = new URL(window.location.href).searchParams;
      return !params.has('variant') && !params.has('option_values');
    } catch {
      return true;
    }
  }

  #selectDefaultQueenCategory() {
    if (!this.#shouldSelectDefaultQueenCategory()) return;

    const categoryIndex = this.#getCategoryFieldsetIndex();
    if (categoryIndex === -1) return;

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const categoryFieldset = fieldsets[categoryIndex];
    if (!categoryFieldset) return;

    const selected = /** @type {HTMLInputElement | null} */ (categoryFieldset.querySelector('input:checked'));
    if (selected?.value?.trim().toLowerCase() === 'queen') return;

    const queenRadio = /** @type {HTMLInputElement | undefined} */ (
      this.#radios[categoryIndex]?.find((radio) => radio.value?.trim().toLowerCase() === 'queen' && !radio.disabled)
    );

    if (!queenRadio) return;
    queenRadio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #getSelectedStringValues() {
    return (/** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []))
      .map(fieldset =>
        /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value ??
        /** @type {HTMLSelectElement | null} */ (fieldset.querySelector('select'))?.value ??
        ''
      )
      .filter(Boolean);
  }

  #findVariantForSelection() {
    const vals = this.#getSelectedStringValues();
    if (!vals.length || !this.#allVariants.length) return null;
    return /** @type {Record<string,unknown>} */ (
      this.#allVariants.find(
        /** @param {Record<string,unknown>} v */
        (v) => vals.every((val, i) => v[`option${i + 1}`] === val)
      ) ?? null
    );
  }

  /**
   * @param {number} fieldsetIndex
   * @returns {string}
   */
  #getFieldsetName(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    return fieldset?.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
  }

  /**
   * @param {number} fieldsetIndex
   * @returns {boolean}
   */
  #isDimensionFieldset(fieldsetIndex) {
    return this.#getFieldsetName(fieldsetIndex).includes('dimension');
  }

  /** @returns {number} */
  #getCategoryFieldsetIndex() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    return fieldsets.findIndex((_, index) => this.#getFieldsetName(index).includes('category'));
  }

  /** @returns {string} */
  #getSelectedCategory() {
    const categoryIndex = this.#getCategoryFieldsetIndex();
    if (categoryIndex === -1) return '';

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const selected = /** @type {HTMLInputElement | null} */ (fieldsets[categoryIndex]?.querySelector('input:checked'));
    return selected?.value?.toLowerCase() || '';
  }

  /**
   * @param {string} value
   * @returns {number | null}
   */
  #getDimensionWidth(value) {
    const matches = String(value).match(/\d+/g);
    if (!matches || matches.length < 2) return null;
    return Number.parseInt(matches[1], 10);
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #isDimensionForSelectedCategory(value) {
    const category = this.#getSelectedCategory();
    const width = this.#getDimensionWidth(value);

    if (!category || !width) return true;

    if (category.includes('single')) return [30, 36, 42].includes(width);
    if (category.includes('double')) return width === 48;
    if (category.includes('queen')) return [60, 66].includes(width);
    if (category.includes('king')) return [72, 78, 84].includes(width);

    return true;
  }

  /**
   * @param {Record<string,unknown>} variant
   * @param {string} [optionId]
   */
  #dispatchOptimisticUpdate(variant, optionId = this.selectedOptionId) {
    if (!variant || !optionId) return;
    document.querySelectorAll('.emi_button').forEach((emiButton) => {
      if (emiButton instanceof HTMLElement) {
        const basePrice = Number(variant.price) / 100;
        const discount = Number(emiButton.dataset.discount) || 0;
        emiButton.dataset.price = String(discount > 0 ? (basePrice * (100 - discount)) / 100 : basePrice);
      }
    });
    const fakeHtml = new DOMParser().parseFromString(
      `<variant-picker class="variant-picker"><script type="application/json">${JSON.stringify(variant)}<\/script></variant-picker>`,
      'text/html'
    );
    this.dispatchEvent(
      new VariantUpdateEvent(
        /** @type {any} */ (variant),
        optionId,
        { html: fakeHtml, productId: this.dataset.productId ?? '', newProduct: undefined }
      )
    );
  }

  // ── Core variant change flow ─────────────────────────────────────────────

  /**
   * Handles the variant change event.
   * @param {Event} event
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    // Filter dependent option groups first (instant, client-side) so the
    // optimistic update and the fetch URL both reflect the final resolved
    // selection. Pass scheduleRefetch:false — this caller owns the fetch.
    this.updateAllAvailability({ scheduleRefetch: false });

    // Find the resolved variant AFTER filtering (auto-selection may have
    // changed the Dimension, so we must read the DOM now).
    const optimisticVariant = this.#findVariantForSelection();

    if (optimisticVariant) {
      // We have client-side variant data — update the button state instantly
      // without disabling it first. VariantUpdateEvent sets variantId + state.
      this.#dispatchOptimisticUpdate(optimisticVariant);
    } else {
      // No client-side data (allVariants not loaded). Disable the button so
      // the user cannot submit with a stale variant ID while the fetch runs.
      this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    // Morph main for combined-listing child products; otherwise morph the
    // featured-product section or just the variant picker.
    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    // Build the URL after filtering so selectedOptionsValues includes any
    // auto-selected option values from updateAllAvailability above.
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);
    const variantId = optimisticVariant?.id ? String(optimisticVariant.id) : selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  // ── Fieldset / pill animation CSS ───────────────────────────────────────

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /** @param {FieldsetMeasurements} measurements */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /** @param {number} fieldsetIndex */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;
    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) this.#applyFieldsetMeasurements(measurements);
  }

  // ── Selected-option state management ────────────────────────────────────

  /**
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          const newCurrentIndex = checkedIndices[0];
          const newPreviousIndex = checkedIndices[1];

          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.#updateSelectedOptionLabel(fieldset, target.value);
          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }
      newSelectedOption.setAttribute('selected', 'selected');
      this.#updateSelectedOptionLabel(target.closest('.variant-option'), newValue);
    }
  }

  /**
   * Updates the visible selected option text immediately, without waiting for the section fetch.
   *
   * @param {Element | null} container
   * @param {string} value
   */
  #updateSelectedOptionLabel(container, value) {
    const selectedValue = container?.querySelector('.variant-option__swatch-value');
    if (selectedValue) selectedValue.textContent = value;
  }

  // ── Availability filtering for all option fieldsets ─────────────────────

  /**
   * @param {{ scheduleRefetch?: boolean }} [options]
   */
  updateAllAvailability({ scheduleRefetch = true } = {}) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    if (!fieldsets.length || !this.#allVariants.length) return;

    /** @type {HTMLInputElement | null} */
    let lastAutoSelectedRadio = null;

    for (let fi = 0; fi < fieldsets.length; fi++) {
      const autoSelected = this.#updateFieldsetAvailability(fi);
      if (autoSelected) lastAutoSelectedRadio = autoSelected;
    }

    this.updateVariantPickerCss();

    // Only schedule an independent fetch when the caller doesn't own one.
    // In variantChanged(), the caller schedules its own fetch after this runs.
    if (scheduleRefetch && lastAutoSelectedRadio) {
      const radio = /** @type {HTMLInputElement} */ (lastAutoSelectedRadio);
      setTimeout(() => {
        this.fetchUpdatedSection(this.buildRequestUrl(radio), undefined);
      }, 50);
    }
  }

  /**
   * Shows/hides options in one fieldset based on which values have at least
   * one available variant given the already-selected preceding options.
   * Auto-selects the first available option if the current one is hidden.
   * Returns the auto-selected radio, or null if no change was needed.
   * @param {number} fieldsetIndex
   * @returns {HTMLInputElement | null}
   */
  #updateFieldsetAvailability(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!fieldset || !radios) return null;

    // Preceding selected values drive which options in this fieldset are reachable.
    const selectedVals = this.#getSelectedStringValues();
    const precedingVals = selectedVals.slice(0, fieldsetIndex);

    /** @type {HTMLInputElement | null} */
    let firstAvailableRadio = null;
    let hasAvailableOptions = false;

    radios.forEach((radio) => {
      const isVariantAvailable = this.#allVariants.some(
        /** @param {Record<string,unknown>} v */
        (v) =>
          precedingVals.every((val, i) => v[`option${i + 1}`] === val) &&
          v[`option${fieldsetIndex + 1}`] === radio.value &&
          v['available'] === true
      );
      const isAvailable =
        isVariantAvailable &&
        (!this.#isDimensionFieldset(fieldsetIndex) || this.#isDimensionForSelectedCategory(radio.value));

      const parentLabel = /** @type {HTMLElement | null} */ (
        radio.closest('label, .option-wrapper, .variant-option')
      );

      if (parentLabel) {
        parentLabel.style.display = isAvailable ? '' : 'none';
        if (
          isAvailable &&
          !parentLabel.classList.contains('variant-option__button-label--has-swatch') &&
          !parentLabel.querySelector('.variant-option__button-label__pill')
        ) {
          const textEl = parentLabel.querySelector('.variant-option__button-label__text');
          const pill = document.createElement('span');
          pill.className = 'variant-option__button-label__pill';
          pill.dataset.key = 'variant-option-pill';
          parentLabel.insertBefore(pill, textEl || radio.nextSibling);
        }
        parentLabel.querySelectorAll('.variant-option__strikethrough').forEach((strike) => {
          if (strike instanceof HTMLElement || strike instanceof SVGElement) {
            strike.style.display = isAvailable ? 'none' : '';
          }
        });
        if (isAvailable) {
          hasAvailableOptions = true;
          if (!firstAvailableRadio) firstAvailableRadio = radio;
        }
      }

      radio.disabled = !isAvailable;
      radio.dataset.optionAvailable = isAvailable ? 'true' : 'false';
      radio.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
    });

    if (!hasAvailableOptions) {
      fieldset.style.display = 'none';
      return null;
    }

    fieldset.style.display = '';

    const hasSelectedOption = radios.some((r) => r.checked && !r.disabled);
    if (hasSelectedOption || !firstAvailableRadio) return null;

    // Current selection became unavailable — auto-select first available option.
    const selectedRadio = /** @type {HTMLInputElement} */ (firstAvailableRadio);
    radios.forEach((radio) => {
      radio.dataset.currentChecked = 'false';
      radio.dataset.previousChecked = 'false';
    });
    selectedRadio.checked = true;
    selectedRadio.dataset.currentChecked = 'true';

    const fi = Number.parseInt(selectedRadio.dataset.fieldsetIndex || '');
    const ii = Number.parseInt(selectedRadio.dataset.inputIndex || '');

    if (!Number.isNaN(fi) && !Number.isNaN(ii)) {
      const checkedIndices = this.#checkedIndices[fi];
      if (checkedIndices) {
        checkedIndices.unshift(ii);
        checkedIndices.length = Math.min(checkedIndices.length, 2);
      }
      this.updateFieldsetCss(fi);
    }

    this.#updateSelectedOptionLabel(fieldset, selectedRadio.value);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedRadio.dataset.optionValueId ?? '' }));

    // Immediately re-enable the button with correct availability — no waiting
    // for the network fetch.
    const autoVariant = this.#findVariantForSelection();
    if (autoVariant) {
      this.#dispatchOptimisticUpdate(autoVariant, selectedRadio.dataset.optionValueId ?? '');
    }

    if (this.dataset.templateProductMatch === 'true') {
      const variantId = autoVariant?.id ? String(autoVariant.id) : selectedRadio.dataset.variantId || null;
      const url = new URL(window.location.href);

      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }

      if (url.href !== window.location.href) {
        yieldToMainThread().then(() => {
          history.replaceState({}, '', url.toString());
        });
      }
    }

    return selectedRadio;
  }

  // ── Section Rendering API ────────────────────────────────────────────────

  /**
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string}
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * @param {string} requestUrl
   * @param {string} [morphElementSelector]
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html
          .querySelector('.variant-picker script[type="application/json"]')
          ?.textContent;

        if (!textContent) return;

        const variantData = JSON.parse(textContent);

        // Keep every EMI trigger in sync with the selected variant.
        document.querySelectorAll('.emi_button').forEach((emiButton) => {
          if (emiButton instanceof HTMLElement) {
            const basePrice = variantData?.price / 100;
            const discount = Number(emiButton.dataset.discount) || 0;
            emiButton.dataset.price = String(discount > 0 ? (basePrice * (100 - discount)) / 100 : basePrice);
          }
        });

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);
          this.#loadAllVariants();
          this.updateAllAvailability();
          const resolvedVariantData = this.#findVariantForSelection() || variantData;

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(resolvedVariantData, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  // ── DOM morphing helpers ─────────────────────────────────────────────────

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * @param {Document | Element} newHtml
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
    if (!newVariantPickerSource) throw new Error('No new variant picker source found');

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.dataset.key;
      },
    });

    // Re-initialize radio arrays after DOM morph.
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
    });

    this.updateVariantPickerCss();
    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const measurements = fieldsets.map((_, i) => this.#getFieldsetMeasurements(i)).filter((m) => m !== null);
    for (const m of measurements) this.#applyFieldsetMeasurements(m);
  }

  /**
   * @param {Document} newHtml
   * @param {string} elementSelector
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);
    if (!element || !newElement) throw new Error(`No new element source found for ${elementSelector}`);
    morph(element, newElement);
  }

  /** @param {Document} newHtml */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');
    if (!main || !newMain) throw new Error('No new main source found');
    morph(main, newMain);
  }

  // ── Getters ──────────────────────────────────────────────────────────────

  /** @returns {HTMLInputElement | HTMLOptionElement | undefined} */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) return undefined;
    return selectedOption;
  }

  /** @returns {string | undefined} */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;
    if (!optionValueId) throw new Error('No option value ID found');
    return optionValueId;
  }

  /** @returns {string[]} */
  get selectedOptionsValues() {
    /** @type {HTMLElement[]} */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;
      if (!optionValueId) throw new Error('No option value ID found');
      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}




// import { Component } from '@theme/component';
// import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
// import { morph, MORPH_OPTIONS } from '@theme/morph';
// import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

// /**
//  * @typedef {object} VariantPickerRefs
//  * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
//  */

// /**
//  * A custom element that manages a variant picker.
//  *
//  * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
//  * @extends Component<TRefs>
//  */
// export default class VariantPicker extends Component {
//   /** @type {string | undefined} */
//   #pendingRequestUrl;

//   /** @type {AbortController | undefined} */
//   #abortController;

//   /** @type {number[][]} */
//   #checkedIndices = [];

//   /** @type {HTMLInputElement[][]} */
//   #radios = [];

//   /** @type {Array<object>} all variants for instant client-side lookup */
//   #allVariants = [];

//   #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

//   connectedCallback() {
//     super.connectedCallback();
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

//     fieldsets.forEach((fieldset) => {
//       const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
//       this.#radios.push(radios);

//       const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
//       if (initialCheckedIndex !== -1) {
//         this.#checkedIndices.push([initialCheckedIndex]);
//       } else {
//         this.#checkedIndices.push([]);
//       }
//     });

//     this.addEventListener('change', this.variantChanged.bind(this));
//     this.#resizeObserver.observe(this);
//     this.#loadAllVariants();
//     if (this.#selectInitialDefaultCategory('queen')) return;
//     this.updateAllAvailability();
//   }

//   disconnectedCallback() {
//     super.disconnectedCallback();
//     this.#resizeObserver.disconnect();
//   }

//   // ── Optimistic update helpers ────────────────────────────────────────────

//   #loadAllVariants() {
//     const el = this.querySelector('script[data-all-variants]');
//     if (!el?.textContent) return;
//     try { this.#allVariants = JSON.parse(el.textContent); } catch {}
//   }

//   /**
//    * @param {string} preferredValue
//    * @returns {boolean}
//    */
//   #selectInitialDefaultCategory(preferredValue) {
//     if (this.dataset.templateProductMatch !== 'true') return false;
//     if (this.closest('product-card') || this.closest('quick-add-dialog')) return false;
//     if (window.__morningOwlDefaultCategoryApplied === this.dataset.productId) return false;

//     const categoryIndex = this.#getCategoryFieldsetIndex();
//     if (categoryIndex === -1 || !this.#allVariants.length) return false;

//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     const fieldset = fieldsets[categoryIndex];
//     const radios = this.#radios[categoryIndex] || [];
//     if (!fieldset || !radios.length) return false;

//     const normalize = (value) => String(value || '').trim().toLowerCase();
//     const preferred = normalize(preferredValue);
//     const selected = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
//     if (normalize(selected?.value) === preferred) {
//       window.__morningOwlDefaultCategoryApplied = this.dataset.productId;
//       return false;
//     }

//     const preferredRadio = radios.find((radio) => {
//       if (normalize(radio.value) !== preferred || radio.disabled) return false;
//       return this.#allVariants.some(
//         /** @param {Record<string,unknown>} variant */
//         (variant) => variant[`option${categoryIndex + 1}`] === radio.value && variant['available'] === true
//       );
//     });

//     if (!preferredRadio) return false;
//     window.__morningOwlDefaultCategoryApplied = this.dataset.productId;
//     preferredRadio.click();
//     return true;
//   }

//   #getSelectedStringValues() {
//     return (/** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []))
//       .map(fieldset =>
//         /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'))?.value ??
//         /** @type {HTMLSelectElement | null} */ (fieldset.querySelector('select'))?.value ??
//         ''
//       )
//       .filter(Boolean);
//   }

//   #findVariantForSelection() {
//     const vals = this.#getSelectedStringValues();
//     if (!vals.length || !this.#allVariants.length) return null;
//     return /** @type {Record<string,unknown>} */ (
//       this.#allVariants.find(
//         /** @param {Record<string,unknown>} v */
//         (v) => vals.every((val, i) => v[`option${i + 1}`] === val)
//       ) ?? null
//     );
//   }

//   /**
//    * @param {number} fieldsetIndex
//    * @returns {string}
//    */
//   #getFieldsetName(fieldsetIndex) {
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     const fieldset = fieldsets[fieldsetIndex];
//     return fieldset?.querySelector('legend')?.textContent?.trim().toLowerCase() || '';
//   }

//   /**
//    * @param {number} fieldsetIndex
//    * @returns {boolean}
//    */
//   #isDimensionFieldset(fieldsetIndex) {
//     return this.#getFieldsetName(fieldsetIndex).includes('dimension');
//   }

//   /** @returns {number} */
//   #getCategoryFieldsetIndex() {
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     return fieldsets.findIndex((_, index) => this.#getFieldsetName(index).includes('category'));
//   }

//   /** @returns {string} */
//   #getSelectedCategory() {
//     const categoryIndex = this.#getCategoryFieldsetIndex();
//     if (categoryIndex === -1) return '';

//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     const selected = /** @type {HTMLInputElement | null} */ (fieldsets[categoryIndex]?.querySelector('input:checked'));
//     return selected?.value?.toLowerCase() || '';
//   }

//   /**
//    * @param {string} value
//    * @returns {number | null}
//    */
//   #getDimensionWidth(value) {
//     const matches = String(value).match(/\d+/g);
//     if (!matches || matches.length < 2) return null;
//     return Number.parseInt(matches[1], 10);
//   }

//   /**
//    * @param {string} value
//    * @returns {boolean}
//    */
//   #isDimensionForSelectedCategory(value) {
//     const category = this.#getSelectedCategory();
//     const width = this.#getDimensionWidth(value);

//     if (!category || !width) return true;

//     if (category.includes('single')) return [30, 36, 42].includes(width);
//     if (category.includes('double')) return width === 48;
//     if (category.includes('queen')) return [60, 66].includes(width);
//     if (category.includes('king')) return [72, 78, 84].includes(width);

//     return true;
//   }

//   /**
//    * @param {Record<string,unknown>} variant
//    * @param {string} [optionId]
//    */
//   #dispatchOptimisticUpdate(variant, optionId = this.selectedOptionId) {
//     if (!variant || !optionId) return;
//     document.querySelectorAll('.emi_button').forEach((emiButton) => {
//       if (emiButton instanceof HTMLElement) {
//         const basePrice = Number(variant.price) / 100;
//         const discount = Number(emiButton.dataset.discount) || 0;
//         emiButton.dataset.price = String(discount > 0 ? (basePrice * (100 - discount)) / 100 : basePrice);
//       }
//     });
//     const fakeHtml = new DOMParser().parseFromString(
//       `<variant-picker class="variant-picker"><script type="application/json">${JSON.stringify(variant)}<\/script></variant-picker>`,
//       'text/html'
//     );
//     this.dispatchEvent(
//       new VariantUpdateEvent(
//         /** @type {any} */ (variant),
//         optionId,
//         { html: fakeHtml, productId: this.dataset.productId ?? '', newProduct: undefined }
//       )
//     );
//   }

//   // ── Core variant change flow ─────────────────────────────────────────────

//   /**
//    * Handles the variant change event.
//    * @param {Event} event
//    */
//   variantChanged(event) {
//     if (!(event.target instanceof HTMLElement)) return;

//     const selectedOption =
//       event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

//     if (!selectedOption) return;

//     this.updateSelectedOption(event.target);

//     // Filter dependent option groups first (instant, client-side) so the
//     // optimistic update and the fetch URL both reflect the final resolved
//     // selection. Pass scheduleRefetch:false — this caller owns the fetch.
//     this.updateAllAvailability({ scheduleRefetch: false });

//     // Find the resolved variant AFTER filtering (auto-selection may have
//     // changed the Dimension, so we must read the DOM now).
//     const optimisticVariant = this.#findVariantForSelection();

//     if (optimisticVariant) {
//       // We have client-side variant data — update the button state instantly
//       // without disabling it first. VariantUpdateEvent sets variantId + state.
//       this.#dispatchOptimisticUpdate(optimisticVariant);
//     } else {
//       // No client-side data (allVariants not loaded). Disable the button so
//       // the user cannot submit with a stale variant ID while the fetch runs.
//       this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));
//     }

//     const isOnProductPage =
//       this.dataset.templateProductMatch === 'true' &&
//       !event.target.closest('product-card') &&
//       !event.target.closest('quick-add-dialog');

//     const currentUrl = this.dataset.productUrl?.split('?')[0];
//     const newUrl = selectedOption.dataset.connectedProductUrl;
//     const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
//     const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

//     // Morph main for combined-listing child products; otherwise morph the
//     // featured-product section or just the variant picker.
//     const morphElementSelector = loadsNewProduct
//       ? 'main'
//       : isOnFeaturedProductSection
//       ? 'featured-product-information'
//       : undefined;

//     // Build the URL after filtering so selectedOptionsValues includes any
//     // auto-selected option values from updateAllAvailability above.
//     this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

//     const url = new URL(window.location.href);
//     const variantId = optimisticVariant?.id ? String(optimisticVariant.id) : selectedOption.dataset.variantId || null;

//     if (isOnProductPage) {
//       if (variantId) {
//         url.searchParams.set('variant', variantId);
//       } else {
//         url.searchParams.delete('variant');
//       }
//     }

//     if (loadsNewProduct) {
//       url.pathname = newUrl;
//     }

//     if (url.href !== window.location.href) {
//       yieldToMainThread().then(() => {
//         history.replaceState({}, '', url.toString());
//       });
//     }
//   }

//   // ── Fieldset / pill animation CSS ───────────────────────────────────────

//   /**
//    * @typedef {object} FieldsetMeasurements
//    * @property {HTMLFieldSetElement} fieldset
//    * @property {number | undefined} currentIndex
//    * @property {number | undefined} previousIndex
//    * @property {number | undefined} currentWidth
//    * @property {number | undefined} previousWidth
//    */

//   /**
//    * @param {number} fieldsetIndex
//    * @returns {FieldsetMeasurements | null}
//    */
//   #getFieldsetMeasurements(fieldsetIndex) {
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     const fieldset = fieldsets[fieldsetIndex];
//     const checkedIndices = this.#checkedIndices[fieldsetIndex];
//     const radios = this.#radios[fieldsetIndex];

//     if (!radios || !checkedIndices || !fieldset) return null;

//     const [currentIndex, previousIndex] = checkedIndices;

//     return {
//       fieldset,
//       currentIndex,
//       previousIndex,
//       currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
//       previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
//     };
//   }

//   /** @param {FieldsetMeasurements} measurements */
//   #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
//     if (currentWidth) {
//       fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
//     } else if (currentIndex !== undefined) {
//       fieldset.style.removeProperty('--pill-width-current');
//     }

//     if (previousWidth) {
//       fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
//     } else if (previousIndex !== undefined) {
//       fieldset.style.removeProperty('--pill-width-previous');
//     }
//   }

//   /** @param {number} fieldsetIndex */
//   updateFieldsetCss(fieldsetIndex) {
//     if (Number.isNaN(fieldsetIndex)) return;
//     const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
//     if (measurements) this.#applyFieldsetMeasurements(measurements);
//   }

//   // ── Selected-option state management ────────────────────────────────────

//   /**
//    * @param {string | Element} target
//    */
//   updateSelectedOption(target) {
//     if (typeof target === 'string') {
//       const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
//       if (!targetElement) throw new Error('Target element not found');
//       target = targetElement;
//     }

//     if (target instanceof HTMLInputElement) {
//       const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
//       const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

//       if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
//         const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//         const fieldset = fieldsets[fieldsetIndex];
//         const checkedIndices = this.#checkedIndices[fieldsetIndex];
//         const radios = this.#radios[fieldsetIndex];

//         if (radios && checkedIndices && fieldset) {
//           const [currentIndex, previousIndex] = checkedIndices;

//           if (currentIndex !== undefined && radios[currentIndex]) {
//             radios[currentIndex].dataset.previousChecked = 'false';
//           }
//           if (previousIndex !== undefined && radios[previousIndex]) {
//             radios[previousIndex].dataset.previousChecked = 'false';
//           }

//           checkedIndices.unshift(inputIndex);
//           checkedIndices.length = Math.min(checkedIndices.length, 2);

//           const newCurrentIndex = checkedIndices[0];
//           const newPreviousIndex = checkedIndices[1];

//           if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
//             radios[newCurrentIndex].dataset.currentChecked = 'true';
//           }

//           if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
//             radios[newPreviousIndex].dataset.previousChecked = 'true';
//             radios[newPreviousIndex].dataset.currentChecked = 'false';
//           }

//           this.#updateSelectedOptionLabel(fieldset, target.value);
//           this.updateFieldsetCss(fieldsetIndex);
//         }
//       }
//       target.checked = true;
//     }

//     if (target instanceof HTMLSelectElement) {
//       const newValue = target.value;
//       const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
//       if (!newSelectedOption) throw new Error('Option not found');

//       for (const option of target.options) {
//         option.removeAttribute('selected');
//       }
//       newSelectedOption.setAttribute('selected', 'selected');
//       this.#updateSelectedOptionLabel(target.closest('.variant-option'), newValue);
//     }
//   }

//   /**
//    * Updates the visible selected option text immediately, without waiting for the section fetch.
//    *
//    * @param {Element | null} container
//    * @param {string} value
//    */
//   #updateSelectedOptionLabel(container, value) {
//     const selectedValue = container?.querySelector('.variant-option__swatch-value');
//     if (selectedValue) selectedValue.textContent = value;
//   }

//   // ── Availability filtering for all option fieldsets ─────────────────────

//   /**
//    * @param {{ scheduleRefetch?: boolean }} [options]
//    */
//   updateAllAvailability({ scheduleRefetch = true } = {}) {
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     if (!fieldsets.length || !this.#allVariants.length) return;

//     /** @type {HTMLInputElement | null} */
//     let lastAutoSelectedRadio = null;

//     for (let fi = 0; fi < fieldsets.length; fi++) {
//       const autoSelected = this.#updateFieldsetAvailability(fi);
//       if (autoSelected) lastAutoSelectedRadio = autoSelected;
//     }

//     this.updateVariantPickerCss();

//     // Only schedule an independent fetch when the caller doesn't own one.
//     // In variantChanged(), the caller schedules its own fetch after this runs.
//     if (scheduleRefetch && lastAutoSelectedRadio) {
//       const radio = /** @type {HTMLInputElement} */ (lastAutoSelectedRadio);
//       setTimeout(() => {
//         this.fetchUpdatedSection(this.buildRequestUrl(radio), undefined);
//       }, 50);
//     }
//   }

//   /**
//    * Shows/hides options in one fieldset based on which values have at least
//    * one available variant given the already-selected preceding options.
//    * Auto-selects the first available option if the current one is hidden.
//    * Returns the auto-selected radio, or null if no change was needed.
//    * @param {number} fieldsetIndex
//    * @returns {HTMLInputElement | null}
//    */
//   #updateFieldsetAvailability(fieldsetIndex) {
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     const fieldset = fieldsets[fieldsetIndex];
//     const radios = this.#radios[fieldsetIndex];

//     if (!fieldset || !radios) return null;

//     // Preceding selected values drive which options in this fieldset are reachable.
//     const selectedVals = this.#getSelectedStringValues();
//     const precedingVals = selectedVals.slice(0, fieldsetIndex);

//     /** @type {HTMLInputElement | null} */
//     let firstAvailableRadio = null;
//     let hasAvailableOptions = false;

//     radios.forEach((radio) => {
//       const isVariantAvailable = this.#allVariants.some(
//         /** @param {Record<string,unknown>} v */
//         (v) =>
//           precedingVals.every((val, i) => v[`option${i + 1}`] === val) &&
//           v[`option${fieldsetIndex + 1}`] === radio.value &&
//           v['available'] === true
//       );
//       const isAvailable =
//         isVariantAvailable &&
//         (!this.#isDimensionFieldset(fieldsetIndex) || this.#isDimensionForSelectedCategory(radio.value));

//       const parentLabel = /** @type {HTMLElement | null} */ (
//         radio.closest('label, .option-wrapper, .variant-option')
//       );

//       if (parentLabel) {
//         parentLabel.style.display = isAvailable ? '' : 'none';
//         if (
//           isAvailable &&
//           !parentLabel.classList.contains('variant-option__button-label--has-swatch') &&
//           !parentLabel.querySelector('.variant-option__button-label__pill')
//         ) {
//           const textEl = parentLabel.querySelector('.variant-option__button-label__text');
//           const pill = document.createElement('span');
//           pill.className = 'variant-option__button-label__pill';
//           pill.dataset.key = 'variant-option-pill';
//           parentLabel.insertBefore(pill, textEl || radio.nextSibling);
//         }
//         parentLabel.querySelectorAll('.variant-option__strikethrough').forEach((strike) => {
//           if (strike instanceof HTMLElement || strike instanceof SVGElement) {
//             strike.style.display = isAvailable ? 'none' : '';
//           }
//         });
//         if (isAvailable) {
//           hasAvailableOptions = true;
//           if (!firstAvailableRadio) firstAvailableRadio = radio;
//         }
//       }

//       radio.disabled = !isAvailable;
//       radio.dataset.optionAvailable = isAvailable ? 'true' : 'false';
//       radio.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
//     });

//     if (!hasAvailableOptions) {
//       fieldset.style.display = 'none';
//       return null;
//     }

//     fieldset.style.display = '';

//     const hasSelectedOption = radios.some((r) => r.checked && !r.disabled);
//     if (hasSelectedOption || !firstAvailableRadio) return null;

//     // Current selection became unavailable — auto-select first available option.
//     const selectedRadio = /** @type {HTMLInputElement} */ (firstAvailableRadio);
//     radios.forEach((radio) => {
//       radio.dataset.currentChecked = 'false';
//       radio.dataset.previousChecked = 'false';
//     });
//     selectedRadio.checked = true;
//     selectedRadio.dataset.currentChecked = 'true';

//     const fi = Number.parseInt(selectedRadio.dataset.fieldsetIndex || '');
//     const ii = Number.parseInt(selectedRadio.dataset.inputIndex || '');

//     if (!Number.isNaN(fi) && !Number.isNaN(ii)) {
//       const checkedIndices = this.#checkedIndices[fi];
//       if (checkedIndices) {
//         checkedIndices.unshift(ii);
//         checkedIndices.length = Math.min(checkedIndices.length, 2);
//       }
//       this.updateFieldsetCss(fi);
//     }

//     this.#updateSelectedOptionLabel(fieldset, selectedRadio.value);
//     this.dispatchEvent(new VariantSelectedEvent({ id: selectedRadio.dataset.optionValueId ?? '' }));

//     // Immediately re-enable the button with correct availability — no waiting
//     // for the network fetch.
//     const autoVariant = this.#findVariantForSelection();
//     if (autoVariant) {
//       this.#dispatchOptimisticUpdate(autoVariant, selectedRadio.dataset.optionValueId ?? '');
//     }

//     if (this.dataset.templateProductMatch === 'true') {
//       const variantId = autoVariant?.id ? String(autoVariant.id) : selectedRadio.dataset.variantId || null;
//       const url = new URL(window.location.href);

//       if (variantId) {
//         url.searchParams.set('variant', variantId);
//       } else {
//         url.searchParams.delete('variant');
//       }

//       if (url.href !== window.location.href) {
//         yieldToMainThread().then(() => {
//           history.replaceState({}, '', url.toString());
//         });
//       }
//     }

//     return selectedRadio;
//   }

//   // ── Section Rendering API ────────────────────────────────────────────────

//   /**
//    * @param {HTMLElement} selectedOption
//    * @param {string | null} [source]
//    * @param {string[]} [sourceSelectedOptionsValues]
//    * @returns {string}
//    */
//   buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
//     let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
//     this.#pendingRequestUrl = productUrl;
//     const params = [];
//     const viewParamValue = getViewParameterValue();

//     if (viewParamValue) params.push(`view=${viewParamValue}`);

//     if (this.selectedOptionsValues.length && !source) {
//       params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
//     } else if (source === 'product-card') {
//       if (this.selectedOptionsValues.length) {
//         params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
//       } else {
//         params.push(`option_values=${selectedOption.dataset.optionValueId}`);
//       }
//     }

//     const SECTION_ID_MAP = {
//       'quick-add-component': 'section-rendering-product-card',
//       'swatches-variant-picker-component': 'section-rendering-product-card',
//       'featured-product-information': this.closest('featured-product-information')?.id,
//     };

//     const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
//       Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
//     );

//     if (closestSectionId) {
//       if (productUrl?.includes('?')) productUrl = productUrl.split('?')[0];
//       return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
//     }

//     return `${productUrl}?${params.join('&')}`;
//   }

//   /**
//    * @param {string} requestUrl
//    * @param {string} [morphElementSelector]
//    */
//   fetchUpdatedSection(requestUrl, morphElementSelector) {
//     this.#abortController?.abort();
//     this.#abortController = new AbortController();

//     fetch(requestUrl, { signal: this.#abortController.signal })
//       .then((response) => response.text())
//       .then((responseText) => {
//         this.#pendingRequestUrl = undefined;
//         const html = new DOMParser().parseFromString(responseText, 'text/html');
//         html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

//         const textContent = html
//           .querySelector('.variant-picker script[type="application/json"]')
//           ?.textContent;

//         if (!textContent) return;

//         const variantData = JSON.parse(textContent);

//         // Keep every EMI trigger in sync with the selected variant.
//         document.querySelectorAll('.emi_button').forEach((emiButton) => {
//           if (emiButton instanceof HTMLElement) {
//             const basePrice = variantData?.price / 100;
//             const discount = Number(emiButton.dataset.discount) || 0;
//             emiButton.dataset.price = String(discount > 0 ? (basePrice * (100 - discount)) / 100 : basePrice);
//           }
//         });

//         if (morphElementSelector === 'main') {
//           this.updateMain(html);
//         } else if (morphElementSelector) {
//           this.updateElement(html, morphElementSelector);
//         } else {
//           const newProduct = this.updateVariantPicker(html);
//           this.#loadAllVariants();
//           this.updateAllAvailability();
//           const resolvedVariantData = this.#findVariantForSelection() || variantData;

//           if (this.selectedOptionId) {
//             this.dispatchEvent(
//               new VariantUpdateEvent(resolvedVariantData, this.selectedOptionId, {
//                 html,
//                 productId: this.dataset.productId ?? '',
//                 newProduct,
//               })
//             );
//           }
//         }
//       })
//       .catch((error) => {
//         if (error.name === 'AbortError') {
//           console.warn('Fetch aborted by user');
//         } else {
//           console.error(error);
//         }
//       });
//   }

//   // ── DOM morphing helpers ─────────────────────────────────────────────────

//   /**
//    * @typedef {Object} NewProduct
//    * @property {string} id
//    * @property {string} url
//    */

//   /**
//    * @param {Document | Element} newHtml
//    * @returns {NewProduct | undefined}
//    */
//   updateVariantPicker(newHtml) {
//     /** @type {NewProduct | undefined} */
//     let newProduct;

//     const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());
//     if (!newVariantPickerSource) throw new Error('No new variant picker source found');

//     if (newVariantPickerSource instanceof HTMLElement) {
//       const newProductId = newVariantPickerSource.dataset.productId;
//       const newProductUrl = newVariantPickerSource.dataset.productUrl;

//       if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
//         newProduct = { id: newProductId, url: newProductUrl };
//       }

//       this.dataset.productId = newProductId;
//       this.dataset.productUrl = newProductUrl;
//     }

//     morph(this, newVariantPickerSource, {
//       ...MORPH_OPTIONS,
//       getNodeKey: (node) => {
//         if (!(node instanceof HTMLElement)) return undefined;
//         return node.dataset.key;
//       },
//     });

//     // Re-initialize radio arrays after DOM morph.
//     this.#radios = [];
//     this.#checkedIndices = [];
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

//     fieldsets.forEach((fieldset) => {
//       const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
//       this.#radios.push(radios);

//       const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
//       this.#checkedIndices.push(initialCheckedIndex !== -1 ? [initialCheckedIndex] : []);
//     });

//     this.updateVariantPickerCss();
//     return newProduct;
//   }

//   updateVariantPickerCss() {
//     const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
//     const measurements = fieldsets.map((_, i) => this.#getFieldsetMeasurements(i)).filter((m) => m !== null);
//     for (const m of measurements) this.#applyFieldsetMeasurements(m);
//   }

//   /**
//    * @param {Document} newHtml
//    * @param {string} elementSelector
//    */
//   updateElement(newHtml, elementSelector) {
//     const element = this.closest(elementSelector);
//     const newElement = newHtml.querySelector(elementSelector);
//     if (!element || !newElement) throw new Error(`No new element source found for ${elementSelector}`);
//     morph(element, newElement);
//   }

//   /** @param {Document} newHtml */
//   updateMain(newHtml) {
//     const main = document.querySelector('main');
//     const newMain = newHtml.querySelector('main');
//     if (!main || !newMain) throw new Error('No new main source found');
//     morph(main, newMain);
//   }

//   // ── Getters ──────────────────────────────────────────────────────────────

//   /** @returns {HTMLInputElement | HTMLOptionElement | undefined} */
//   get selectedOption() {
//     const selectedOption = this.querySelector('select option[selected], fieldset input:checked');
//     if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) return undefined;
//     return selectedOption;
//   }

//   /** @returns {string | undefined} */
//   get selectedOptionId() {
//     const { selectedOption } = this;
//     if (!selectedOption) return undefined;
//     const { optionValueId } = selectedOption.dataset;
//     if (!optionValueId) throw new Error('No option value ID found');
//     return optionValueId;
//   }

//   /** @returns {string[]} */
//   get selectedOptionsValues() {
//     /** @type {HTMLElement[]} */
//     const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
//     return selectedOptions.map((option) => {
//       const { optionValueId } = option.dataset;
//       if (!optionValueId) throw new Error('No option value ID found');
//       return optionValueId;
//     });
//   }
// }

// if (!customElements.get('variant-picker')) {
//   customElements.define('variant-picker', VariantPicker);
// }
